// frontend/src/store/authStore.ts

import { create } from 'zustand';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { Profile } from '../types/auth.types';

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isRecovery: boolean;
  setRecovery: (val: boolean) => void;
  /** Supabase auth listener'ı kurar; uygulama kök component'inde bir kez çağrılmalı. */
  initialize: () => () => void;
  fetchProfile: () => Promise<void>;
  reset: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  profile: null,
  isLoading: true,
  isRecovery: false,

  setRecovery: (val: boolean) => set({ isRecovery: val }),

  initialize: () => {
    // Sadece gerçek şifre sıfırlama URL'lerinde isRecovery=true ayarla.
    // Supabase'in kayıt onay maili de access_token içerir — onu yakalamamak gerekir.
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const isRecoveryInit =
      hash.includes('type=recovery') ||
      search.includes('type=recovery') ||
      search.includes('mode=reset-password') ||
      window.location.pathname === '/reset-password';

    if (isRecoveryInit) {
      set({ isRecovery: true });
    }

    // Açılışta mevcut oturumu al
    supabase.auth.getSession()
      .then(({ data }) => {
        set({ session: data?.session ?? null, isLoading: false });
        if (data?.session) get().fetchProfile();
      })
      .catch((err) => {
        console.warn('Session init error:', err);
        set({ session: null, isLoading: false });
      });

    // Oturum değişikliklerini dinle (login/logout/token yenileme)
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        set({ isRecovery: true });
      }

      const currentSession = get().session;
      const isTokenDifferent = currentSession?.access_token !== session?.access_token;
      const isUserDifferent = currentSession?.user?.id !== session?.user?.id;
      const hasSessionStateChanged = (!currentSession && session) || (currentSession && !session);

      if (isTokenDifferent || isUserDifferent || hasSessionStateChanged) {
        set({ session, isLoading: false });
        if (session) {
          get().fetchProfile();
        } else {
          set({ profile: null });
        }
      } else {
        // Oturum değişmese bile eğer yükleme durumundaysak kapat
        if (get().isLoading) {
          set({ isLoading: false });
        }
      }
    });

    return () => listener?.subscription?.unsubscribe();
  },

  fetchProfile: async () => {
    const userId = get().session?.user.id;
    if (!userId) return;

    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
      if (error) {
        console.error('Profil alınamadı:', error.message);
        return;
      }
      set({ profile: data as Profile });
    } catch (err) {
      console.warn('Fetch profile error:', err);
    }
  },

  reset: () => set({ session: null, profile: null, isLoading: false, isRecovery: false }),
}));
